import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, AlertCircle, Loader2, TicketPlus } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        type: 'ai',
        text: "Hi! 👋 I'm UPost Support AI. I'm here to help you with any questions about our platform, features, or troubleshooting. How can I assist you today?",
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    setMessages(prev => [...prev, {
      type: 'user',
      text: userMessage,
      timestamp: new Date()
    }]);

    setIsLoading(true);

    try {
      const response = await api.request('/ai-chat/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (response.success) {
        // Save session ID for conversation continuity
        if (response.session_id && !sessionId) {
          setSessionId(response.session_id);
        }

        // Add AI response to chat
        setMessages(prev => [...prev, {
          type: 'ai',
          text: response.response,
          timestamp: new Date()
        }]);
      } else {
        // Error response
        setMessages(prev => [...prev, {
          type: 'error',
          text: response.response || "Sorry, I'm having trouble responding. Please try again or create a support ticket.",
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        text: "I'm having connection issues. Please try again or create a support ticket for immediate assistance.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateTicket = () => {
    setShowTicketForm(true);
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const ticketData = {
      subject: formData.get('subject'),
      message: formData.get('message'),
      category: formData.get('category'),
      chat_history: messages.map(msg => ({
        user: msg.type === 'user' ? msg.text : '',
        ai: msg.type === 'ai' ? msg.text : ''
      }))
    };

    try {
      const response = await api.request('/ai-chat/create-ticket-from-chat', {
        method: 'POST',
        body: JSON.stringify(ticketData)
      });

      if (response.success) {
        alert(`✅ Support ticket #${response.ticket_id.slice(0, 8)} created! Our team will respond within 24 hours.`);
        setShowTicketForm(false);
        setIsOpen(false);
        setMessages([]);
      }
    } catch (error) {
      alert('Failed to create ticket. Please try again.');
    }
  };

  if (showTicketForm) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <div className="bg-white rounded-lg shadow-2xl w-96 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center gap-2">
              <TicketPlus className="w-5 h-5" />
              <h3 className="font-semibold">Create Support Ticket</h3>
            </div>
            <button
              onClick={() => setShowTicketForm(false)}
              className="hover:bg-white/20 rounded p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Ticket Form */}
          <form onSubmit={handleSubmitTicket} className="p-4 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                name="category"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="billing">Billing Issue</option>
                <option value="security">Security Issue</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Subject</label>
              <input
                type="text"
                name="subject"
                required
                placeholder="Brief description of the issue"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Details</label>
              <textarea
                name="message"
                required
                rows="4"
                placeholder="Please provide detailed information..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <p className="text-xs text-gray-500">
              💬 Your chat history will be included to help us understand the context.
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowTicketForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Submit Ticket
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isOpen ? (
        <div className="bg-white rounded-lg shadow-2xl w-96 h-[600px] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <div>
                <h3 className="font-semibold">UPost Support AI</h3>
                <p className="text-xs opacity-90">Usually replies instantly</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                    msg.type === 'user'
                      ? 'bg-purple-600 text-white'
                      : msg.type === 'error'
                      ? 'bg-red-50 text-red-900 border border-red-200'
                      : 'bg-white text-gray-900 shadow-sm'
                  }`}
                >
                  {msg.type === 'error' && (
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Action Buttons */}
          <div className="p-2 bg-gray-100 flex gap-2">
            <button
              onClick={handleCreateTicket}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              disabled={!user}
              title={!user ? "Please login to create a ticket" : ""}
            >
              <TicketPlus className="w-4 h-4" />
              Create Ticket
            </button>
            {messages.length > 1 && (
              <button
                onClick={() => {
                  setMessages([messages[0]]);
                  setSessionId(null);
                }}
                className="flex-1 text-sm text-gray-600 hover:text-gray-900"
              >
                New Conversation
              </button>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-600"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="bg-purple-600 text-white p-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center gap-2"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="font-medium">Need Help?</span>
        </button>
      )}
    </div>
  );
}
